import { useState } from "react"
import { Head, Link, router, usePage } from "@inertiajs/react"
import { PublicShell } from "@/components/PublicShell"
import { Button } from "@/components/ui/button"

type SelectOpt = { id: number; name?: string; title?: string }
type LinkFormProps = { id: number | null; link_text: string; url: string; title: string | null; details: string | null; linkable_type: string; linkable_id: number | null }
type PageProps = {
  link: LinkFormProps
  bands: SelectOpt[]
  people: SelectOpt[]
  recordings: Array<{ id: number; title: string }>
  venues: SelectOpt[]
  return_to?: string | null
  errors?: Record<string, string>
}

const ic = "w-full rounded border border-hairline bg-page px-3 py-2 text-sm text-ink-body focus:outline-none focus:ring-1 focus:ring-primary"

export default function LinkForm({ link, bands, people, recordings, venues, return_to }: {
  link: LinkFormProps
  bands: SelectOpt[]
  people: SelectOpt[]
  recordings: Array<{ id: number; title: string }>
  venues: SelectOpt[]
  return_to?: string | null
}) {
  const { errors = {} } = usePage<PageProps>().props
  const isNew = link.id === null
  const [form, setForm] = useState({
    link_text:    link.link_text ?? "",
    url:          link.url ?? "",
    title:        link.title ?? "",
    details:      link.details ?? "",
    linkable_type: link.linkable_type ?? "Band",
    linkable_id:  link.linkable_id !== null ? String(link.linkable_id) : "",
  })

  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }))
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const payload = return_to ? { link: form, return_to } : { link: form }
    if (isNew) router.post("/links", payload)
    else       router.patch(`/links/${link.id}`, payload)
  }

  const linkableOptions: Array<{ id: number; label: string }> = form.linkable_type === "Band"
    ? bands.map((b) => ({ id: b.id, label: b.name ?? "" }))
    : form.linkable_type === "Person"
    ? people.map((p) => ({ id: p.id, label: p.name ?? "" }))
    : form.linkable_type === "Recording"
    ? recordings.map((r) => ({ id: r.id, label: r.title }))
    : venues.map((v) => ({ id: v.id, label: v.name ?? "" }))

  const pageTitle = isNew ? "New link" : `Edit ${link.link_text}`
  const cancelHref = return_to ?? "/"

  return (
    <>
      <Head title={pageTitle}>
        <meta name="description" content={pageTitle} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageTitle} />
      </Head>
      <PublicShell>
        <div className="border-b border-hairline pb-6"><h1>{pageTitle}</h1></div>
        <form onSubmit={handleSubmit} className="mt-6 max-w-xl space-y-4">
          <div>
            <label className="block text-sm text-ink-muted mb-1">Link text</label>
            <input name="link_text" type="text" value={form.link_text} onChange={handleChange} className={ic} />
            {errors.link_text && <p className="mt-1 text-xs text-red-600">{errors.link_text}</p>}
          </div>
          <div>
            <label className="block text-sm text-ink-muted mb-1">URL</label>
            <input name="url" type="text" value={form.url} onChange={handleChange} className={ic} />
            {errors.url && <p className="mt-1 text-xs text-red-600">{errors.url}</p>}
          </div>
          <div>
            <label className="block text-sm text-ink-muted mb-1">Title</label>
            <input name="title" type="text" value={form.title} onChange={handleChange} className={ic} />
          </div>
          <div>
            <label className="block text-sm text-ink-muted mb-1">Details</label>
            <textarea name="details" rows={3} value={form.details} onChange={handleChange} className={ic} />
          </div>
          <div>
            <label className="block text-sm text-ink-muted mb-1">Attached to type</label>
            <select name="linkable_type" value={form.linkable_type} onChange={handleChange} className={ic}>
              <option value="Band">Band</option>
              <option value="Person">Person</option>
              <option value="Recording">Recording</option>
              <option value="Venue">Venue</option>
            </select>
          </div>
          <div>
            <label className="block text-sm text-ink-muted mb-1">Attached to</label>
            <select name="linkable_id" value={form.linkable_id} onChange={handleChange} className={ic}>
              <option value="">— select —</option>
              {linkableOptions.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-3 pt-2">
            <Button type="submit">{isNew ? "Create link" : "Update link"}</Button>
            <Link href={cancelHref} className="text-sm text-ink-muted no-underline hover:text-ink-display">Cancel</Link>
          </div>
        </form>
      </PublicShell>
    </>
  )
}
